import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';
import { toast } from 'react-toastify';
import { AuthState, LoginCredentials, RegisterData, AuthResponse, User, transformApiUserToFrontend } from '../../types';
import { AxiosError } from 'axios';

// Async thunks
export const login = createAsyncThunk<AuthResponse, LoginCredentials>(
  'auth/login',
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const response = await api.post('/auth/login', { email, password });
      localStorage.setItem('token', response.data.access_token);
      api.defaults.headers.common['Authorization'] = `Bearer ${response.data.access_token}`;
      
      // Transform user data if present
      const userData = response.data.user ? transformApiUserToFrontend(response.data.user) : null;
      
      return {
        user: userData,
        accessToken: response.data.access_token,
        tokenType: response.data.token_type || 'Bearer'
      };
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      return rejectWithValue(axiosError.response?.data?.message || 'Login failed');
    }
  }
);

export const register = createAsyncThunk<AuthResponse, RegisterData>(
  'auth/register',
  async (userData, { rejectWithValue }) => {
    try {
      // Transform camelCase to snake_case for backend
      const backendData = {
        email: userData.email,
        password: userData.password,
        password_confirmation: userData.password, // Use password as confirmation since validation happened in frontend
        first_name: userData.firstName,
        last_name: userData.lastName,
        phone: userData.phoneNumber,
        address: userData.address,
        captcha_token: userData.captchaToken,
      };

      const response = await api.post('/auth/register', backendData);
      localStorage.setItem('token', response.data.access_token);
      api.defaults.headers.common['Authorization'] = `Bearer ${response.data.access_token}`;
      
      // Transform user data if present
      const transformedUser = response.data.user ? transformApiUserToFrontend(response.data.user) : null;
      
      return {
        user: transformedUser,
        accessToken: response.data.access_token,
        tokenType: response.data.token_type || 'Bearer'
      };
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      return rejectWithValue(axiosError.response?.data?.message || 'Registration failed');
    }
  }
);

export const loadUser = createAsyncThunk<User | null, void>(
  'auth/loadUser',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return null;
      
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      const response = await api.get('/auth/me');
      const userData = response.data.user || response.data;
      
      // Transform API fields to match frontend types using utility function
      if (userData) {
        return transformApiUserToFrontend(userData);
      }
      
      return null;
    } catch (error) {
      localStorage.removeItem('token');
      delete api.defaults.headers.common?.['Authorization'];
      const axiosError = error as AxiosError<{ message: string }>;
      return rejectWithValue(axiosError.response?.data?.message || 'Failed to load user');
    }
  }
);

export const logout = createAsyncThunk<null, void>('auth/logout', async () => {
  localStorage.removeItem('token');
  delete api.defaults.headers.common?.['Authorization'];
  return null;
});

export const updateProfile = createAsyncThunk<User, Partial<User>>(
  'auth/updateProfile',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await api.put('/auth/profile', userData);
      const updatedUserData = response.data.user || response.data;
      
      // Transform API response to frontend format
      if (updatedUserData) {
        return transformApiUserToFrontend(updatedUserData);
      }
      
      return updatedUserData;
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      return rejectWithValue(axiosError.response?.data?.message || 'Update failed');
    }
  }
);

export const forgotPassword = createAsyncThunk<{ message: string }, { email: string }>(
  'auth/forgotPassword',
  async ({ email }, { rejectWithValue }) => {
    try {
      const response = await api.post('/auth/forgot-password', { email });
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      return rejectWithValue(axiosError.response?.data?.message || 'Failed to send reset email');
    }
  }
);

export const validateResetToken = createAsyncThunk<{ message: string }, { token: string; email: string }>(
  'auth/validateResetToken',
  async ({ token, email }, { rejectWithValue }) => {
    try {
      const response = await api.get(`/auth/validate-reset-token?token=${token}&email=${encodeURIComponent(email)}`);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      return rejectWithValue(axiosError.response?.data?.message || 'Invalid or expired token');
    }
  }
);

export const resetPassword = createAsyncThunk<{ message: string }, { token: string; email: string; newPassword: string }>(
  'auth/resetPassword',
  async ({ token, email, newPassword }, { rejectWithValue }) => {
    try {
      const response = await api.post('/auth/reset-password', {
        token,
        email,
        password: newPassword,
        password_confirmation: newPassword
      });
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      return rejectWithValue(axiosError.response?.data?.message || 'Failed to reset password');
    }
  }
);

const initialState: AuthState = {
  user: null,
  token: localStorage.getItem('token'),
  isAuthenticated: false,
  loading: false,
  error: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Login
    builder
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.token = action.payload.accessToken;
        toast.success('Login successful!');
        
        // Merge guest cart items to user account after successful login
        // This will be handled by middleware in the store
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        toast.error(action.payload as string);
      });

    // Register
    builder
      .addCase(register.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(register.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.token = action.payload.accessToken;
        toast.success('Registration successful!');
      })
      .addCase(register.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        toast.error(action.payload as string);
      });

    // Load User
    builder
      .addCase(loadUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(loadUser.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          state.isAuthenticated = true;
          state.user = action.payload;
        }
      })
      .addCase(loadUser.rejected, (state) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
      });

    // Logout
    builder.addCase(logout.fulfilled, (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      toast.info('Logged out successfully');
    });

    // Update Profile
    builder
      .addCase(updateProfile.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        toast.success('Profile updated successfully');
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        toast.error(action.payload as string);
      });

    // Forgot Password
    builder
      .addCase(forgotPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(forgotPassword.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Validate Reset Token
    builder
      .addCase(validateResetToken.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(validateResetToken.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(validateResetToken.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Reset Password
    builder
      .addCase(resetPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resetPassword.fulfilled, (state) => {
        state.loading = false;
        toast.success('Password reset successfully');
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        toast.error(action.payload as string);
      });
  },
});

export const { clearError } = authSlice.actions;
export default authSlice.reducer;